'use client'

import { useTranslation } from '@shared/config/i18n'
import { useMemo, useState } from 'react'

import {
  createRegisterDeliveryInformationFormSchema,
  RegisterDeliveryInformationFormErrors,
  RegisterDeliveryInformationFormState,
  RegisterDeliveryInformationFormValues,
} from '../schemas'

export const useRegisterDeliveryInformationForm = (initialValues: Partial<RegisterDeliveryInformationFormValues>) => {
  const t = useTranslation()
  const registerDeliveryInformationFormSchema = useMemo(() => createRegisterDeliveryInformationFormSchema(t), [t])
  const formKeys = useMemo(
    () => Object.keys(registerDeliveryInformationFormSchema._def.shape()),
    [registerDeliveryInformationFormSchema],
  )

  const initializeFormValues = (keys: string[], initialData: Partial<RegisterDeliveryInformationFormValues>) =>
    keys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: initialData[key as keyof typeof initialData] ?? '',
      }),
      {} as RegisterDeliveryInformationFormValues,
    )

  const initializeFormErrors = (keys: string[], initialData: Partial<RegisterDeliveryInformationFormValues>) =>
    keys.reduce((acc, key) => {
      const schema = registerDeliveryInformationFormSchema._def.shape()[key as keyof typeof initialData]
      const isValid = schema.safeParse(initialData[key as keyof typeof initialData]).success

      return {
        ...acc,
        [key]: {
          hasError: !isValid,
          errorMessage: '',
        },
      }
    }, {} as RegisterDeliveryInformationFormErrors)

  const [formState, setFormState] = useState<RegisterDeliveryInformationFormState>({
    values: initializeFormValues(formKeys, initialValues),
    errors: initializeFormErrors(formKeys, initialValues),
  })

  const validationNumeric = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.replace(/[^\d]/g, '')
  }

  const handleChange = (name: keyof RegisterDeliveryInformationFormValues, value: string) => {
    const result = validateFormItemByName(name as keyof RegisterDeliveryInformationFormValues, value)

    if (result?.success) {
      setFormState((prev) => ({
        values: { ...prev.values, [name]: value },
        errors: { ...prev.errors, [name]: { hasError: false } },
      }))
    } else {
      setFormState((prev) => ({
        values: { ...prev.values, [name]: value },
        errors: { ...prev.errors, [name]: { hasError: true, errorMessage: result?.errorMessage || '' } },
      }))
    }
  }

  const validateFormItemByName = (name: keyof RegisterDeliveryInformationFormValues, value: string) => {
    const validationResult = registerDeliveryInformationFormSchema._def.shape()[name].safeParse(value)

    if (validationResult.success) {
      return { success: true }
    }

    if (validationResult.error) {
      return { success: false, errorMessage: validationResult.error.errors[0].message }
    }
  }

  const isFormValid = formKeys.every(
    (key) => !formState.errors[key as keyof RegisterDeliveryInformationFormErrors].hasError,
  )

  return { formState, validationNumeric, handleChange, isFormValid }
}
